import { MenuItem, FormGroup, Checkbox, Box, Button, DialogActions,
  DialogContent, DialogTitle, Grid,Typography } from '@mui/material';
import React, { FC, useEffect, useMemo, useState, useRef } from 'react';
import Divider from '@mui/material/Divider';
import LoadingButton from '@mui/lab/LoadingButton';
import { styled } from '@mui/material/styles';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import { useAppDispatch, useAppSelector, } from "../../store/hooks";
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from "yup";
import CustomTextField from '../FormFields/CustomTextField';
import { toast } from 'react-toastify';
import CloseIcon from '@mui/icons-material/Close';
import axios from '../../utils/axios';

interface DialogShareSalarySlipProps {
  salaryId: string;
  employeeName: string;
  employeeEmail: string;
  employeePhone: string;
  openDrawer: boolean;
  onClose: () => void;
}

const initialState = {
  email: true,
  sms: false,
  whatsapp: false,
};

interface ChipData {
  key: number;
  label: string;
}

const ListItem = styled('li')(({ theme }) => ({
  margin: theme.spacing(0.5),
}));

const DialogShareSalarySlip: FC<DialogShareSalarySlipProps> = ({
  openDrawer,
  salaryId,
  employeeName,
  employeeEmail,
  employeePhone,
  onClose
}) => {

  const dispatch = useAppDispatch();
  const { selectedAccount } = useAppSelector(state => state.auth);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [pdfUrl, setPdfUrl] = useState<string>("");
  const [state, setState] = useState(initialState);
  const { email, sms, whatsapp } = state;
  const [chipData, setChipData] = useState<readonly ChipData[]>([]);

  // Schema to validate form
  const schema = yup.object().shape({
    toEmail: yup.string().required("Please enter the comma separated email ids. Example: abc@gmail.com, xyz@gamil.com")
  });

  const methods = useForm<any>({ resolver: yupResolver(schema) });

  const handleDeleteChip = (chipToDelete: ChipData) => () => {
    setChipData((chips) => chips.filter((chip) => chip.key !== chipToDelete.key));
  };

  const handleKeyDown = (event: any) => {
    if (["Enter", "Tab", ","].includes(event.key)) {
      var value = event.target.value.trim();
      if (value && checkIsValid(value)) {
        setChipData([...chipData, {key: chipData.length, label: value}])
        methods.setValue("toEmail", '');
      }
      return true;
    }
  };

  const checkIsValid = (emailString: any) => {
    let error = null;
    if (isInList(emailString)) {
      error = `${emailString} has already been added.`;
    }else if (!isEmailValid(emailString)) {
      error = `${emailString} is not a valid email address.`;
    }
    if (error) {
      toast.error(error)
      return false;
    }
    return true;
  }

  const isInList = (emailString: any) => {
    let chips = chipData.map(({ key, label }) => {
      return label;
    });
    return chips.includes(emailString);
  }

  const isEmailValid = (emailString: any) => {
    return /[\w\d\.-]+@[\w\d\.-]+\.[\w\d\.-]+/.test(emailString);
  }

  useEffect(() => {
    // Initialize email chips
    if (employeeEmail) {
      let emailData: any = null;

      if (Array.isArray(employeeEmail)) {
        emailData = employeeEmail;
      } else if (typeof employeeEmail === 'string') {
        emailData = employeeEmail.split(',');
      }

      let chips = [];
      if (emailData) {
        for (let i = 0; i < emailData.length; i++) {
          chips.push({key: i, label: emailData[i].trim()});
        }
      }
      setChipData(chips);
    }

    // Load PDF preview
    const loadPdfPreview = async () => {
      try {
        const response = await axios.get(`/api/v1/salary/${salaryId}/download-slip`, {
          responseType: 'blob'
        });
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        setPdfUrl(url);
      } catch (error) {
        console.error('Failed to load PDF preview:', error);
        toast.error('Failed to load salary slip preview');
      }
    };

    if (salaryId && openDrawer) {
      loadPdfPreview();
    }

    // Cleanup
    return () => {
      if (pdfUrl) {
        window.URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [salaryId, employeeEmail, openDrawer]);

  const handleShareDrawerClose = () => {
    setState(initialState);
    setPdfUrl('');
    onClose();
    setIsLoading(false);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setState({
      ...state,
      [event.target.name]: event.target.checked,
    });
  };

  const onSubmit = async () => {
    setIsLoading(true);

    let mailerList = chipData.map(({ key, label }) => {
      return label;
    });

    if (mailerList.length === 0) {
      toast.error("Please enter atleast one recipient");
      setIsLoading(false);
      return;
    }

    if (!sms && !whatsapp && !email) {
      toast.error("Please check atleast one method (Email, SMS or WhatsApp)");
      setIsLoading(false);
      return;
    }

    try {
      // Build query params
      let queryParams = '';
      if (email) queryParams = `email=${email}`;
      if (sms) queryParams += `&sms=${sms}`;
      if (whatsapp) queryParams += `&whatsapp=${whatsapp}`;

      // Call share API
      const response = await axios.post(`/api/v1/salary/${salaryId}/share?${queryParams}`, {
        toEmailList: mailerList,
        mobile: employeePhone
      });

      if (response.data.success) {
        toast.success('Salary slip shared successfully!');
        methods.reset();
        setChipData([]);
        handleShareDrawerClose();
      } else {
        toast.error(response.data.message || 'Failed to share salary slip');
      }
    } catch (error: any) {
      console.error('Error sharing salary slip:', error);
      toast.error(error.response?.data?.message || 'Failed to share salary slip');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="drawer-form-container">
      <FormProvider {...methods}>
        <Box
          sx={{ display: "flex", flexDirection: "column", height: "100vh", padding: "16px 0 16px 16px" }}
          component="form"
          onSubmit={(e:any) => {e.preventDefault();}}
          noValidate>

            <DialogTitle>
              <Box sx={{ display: "flex", mt: 2, justifyContent: "space-between" }}>
                <Typography variant="h4">Share Salary Slip</Typography>
                <CloseIcon style={{flex: 'end'}} onClick={handleShareDrawerClose} color={'error'} />
              </Box>
              <Divider style={{width: '100%', margin: '20px 5px 20px 0px'}}/>
            </DialogTitle>

            <DialogContent>
            <Grid container justifyContent="center" spacing={2} rowSpacing={-2}>
                <Grid item xs={12} display="flex" justifyContent="flex-end">
                  <Grid container justifyContent="left" spacing={2} rowSpacing={-2}>

                  <Grid item xs={12} md={12} sm={12} lg={12} style={{paddingBottom: "10px"}}>
                    <Paper
                      sx={{
                        display: 'flex',
                        justifyContent: 'left',
                        flexWrap: 'wrap',
                        listStyle: 'none',
                        p: 0.5,
                        m: 0,
                        backgroundColor: '#008aff17'
                      }}
                      component="ul"
                    >
                      {chipData.map((data) => {
                        return (
                          <ListItem key={data.key}>
                            <Chip
                              label={data.label}
                              color='secondary'
                              onDelete={handleDeleteChip(data)}
                            />
                          </ListItem>
                        );
                      })}
                    </Paper>
                  </Grid>

                    <Grid item xs={12} md={12} sm={12} lg={12}>
                      <CustomTextField style={{ margin: '5px 0px 30px 0px' }}
                        variant="outlined"
                        label="Enter comma separated Email IDs"
                        name="toEmail"
                        onKeyDown={(e: any) => handleKeyDown(e)}
                        InputLabelProps={{ shrink: true }} />
                    </Grid>

                    <MenuItem key={'email'}><Checkbox checked={email} onChange={handleChange} name='email'/> {'Email'}</MenuItem>
                    <MenuItem key={'whatsapp'}><Checkbox checked={whatsapp} onChange={handleChange} name='whatsapp'/> {'WhatsApp'}</MenuItem>
                    <MenuItem key={'sms'}><Checkbox checked={sms} onChange={handleChange} name='sms' disabled /> {'SMS'}</MenuItem>

                  </Grid>
                </Grid>
            </Grid>
            <Box>
              <object style={{ height: "70vh", width: "50vw" }} data={pdfUrl} type="application/pdf">
              </object>
            </Box>

            </DialogContent>

            <DialogActions sx={{ height: "40px", mb: 2 }}>
              <Grid container gap='0' sx={{ mr: '32px', pt: '10px' }}>
              <Divider style={{width: '100%', margin: '20px 5px 15px'}}/>
                <Grid item xs={12} md={12} sm={12} lg={12} display="flex" justifyContent="flex-end">

                <Button variant='contained' color='error'
                  style={{ marginRight: '10px' }}
                  onClick={() => {
                    methods.reset();
                    handleShareDrawerClose();
                  }}>Close</Button>
                <LoadingButton
                  type="button"
                  onClick={onSubmit}
                  variant="contained"
                  loading={isLoading}>
                  Share Salary Slip
                </LoadingButton>

                </Grid>
              </Grid>
            </DialogActions>
        </Box>
      </FormProvider>
    </div>
  );
};

export default DialogShareSalarySlip;
